import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

// Aiqbee CRM design system — refined from Platform fluentTheme.ts.
// Improvements over Platform: softer radii (6/10), visible focus rings,
// semantic success/warning/error/info palettes, dark divider contrast fix,
// bordered flat cards with hover lift, overline variant for group labels.

export const stageColors = {
  light: { lead: '#2D9CDB', qualified: '#E05A2B', proposal: '#7B1FA2', won: '#1E8E3E', lost: '#C62828' },
  dark: { lead: '#4DB8E8', qualified: '#FF6B3D', proposal: '#BA68C8', won: '#4CAF50', lost: '#EF5350' },
} as const;

const common: ThemeOptions = {
  typography: {
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', sans-serif",
    h1: { fontSize: 28, fontWeight: 600 },
    h2: { fontSize: 20, fontWeight: 600 },
    h3: { fontSize: 16, fontWeight: 600 },
    h4: { fontSize: 14, fontWeight: 600 },
    h5: { fontSize: 14, fontWeight: 600 },
    h6: { fontSize: 14, fontWeight: 600 },
    body1: { fontSize: 14, lineHeight: 1.6 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    caption: { fontSize: 12, lineHeight: 1.5 },
    overline: { fontSize: 11, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase' },
    button: { fontSize: 14, fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 6 },
};

function componentsFor(theme: Theme): Theme['components'] {
  const dark = theme.palette.mode === 'dark';
  const focusRing = {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: '2px',
  };
  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Global visible focus ring; user-content bidi isolation for RTL data in LTR chrome.
        '*:focus-visible': focusRing,
        '[data-user-content]': { unicodeBidi: 'plaintext' as const },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 6 },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          transition: 'box-shadow 120ms ease, transform 120ms ease',
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 10 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 48 },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 3 } } },
    MuiTextField: {
      styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: 6 } } },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 6, fontWeight: 600 } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: 12, borderRadius: 6, padding: '6px 10px' },
      },
    },
    MuiSnackbarContent: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiTableCell: {
      styleOverrides: { head: { fontWeight: 600, color: theme.palette.text.secondary } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: dark ? '#2D1F1A' : '#FFF5F0',
            '&:hover': { backgroundColor: dark ? '#3A2820' : '#FFEDE4' },
          },
        },
      },
    },
  };
}

function build(palette: ThemeOptions['palette']): Theme {
  const base = createTheme({ ...common, palette });
  return createTheme(base, { components: componentsFor(base) });
}

export const lightTheme = build({
  mode: 'light',
  primary: { main: '#E05A2B', light: '#FFF5F0', dark: '#C74E23', contrastText: '#FFFFFF' },
  secondary: { main: '#2D9CDB', light: '#EBF5FB', contrastText: '#FFFFFF' },
  success: { main: '#1E8E3E', light: '#E9F5EC', contrastText: '#FFFFFF' },
  warning: { main: '#B26A00', light: '#FFF4E0', contrastText: '#FFFFFF' },
  error: { main: '#C62828', light: '#FDECEA', contrastText: '#FFFFFF' },
  info: { main: '#2D9CDB', light: '#EBF5FB', contrastText: '#FFFFFF' },
  background: { default: '#F3F4F6', paper: '#FFFFFF' },
  divider: '#E1E4E8',
  text: { primary: '#242424', secondary: '#5C5C5C', disabled: '#9AA0A6' },
  action: { hover: '#F8F9FA' },
});

export const darkTheme = build({
  mode: 'dark',
  primary: { main: '#FF6B3D', light: '#2D1F1A', dark: '#FF8559', contrastText: '#0F0F0F' },
  secondary: { main: '#4DB8E8', light: '#1A2D3D', contrastText: '#0F0F0F' },
  success: { main: '#4CAF50', light: '#1B2E1C', contrastText: '#0F0F0F' },
  warning: { main: '#FFB74D', light: '#332617', contrastText: '#0F0F0F' },
  error: { main: '#EF5350', light: '#331A1A', contrastText: '#0F0F0F' },
  info: { main: '#4DB8E8', light: '#1A2D3D', contrastText: '#0F0F0F' },
  background: { default: '#0F0F0F', paper: '#1E1E1E' },
  divider: '#3A3A3A',
  text: { primary: '#E0E0E0', secondary: '#A0A0A0', disabled: '#5C5C5C' },
  action: { hover: '#2D2D2D' },
});
